import React, { useContext, useEffect, useMemo, useState } from 'react';
import LeftNavigation from 'src/modules/left-navigation';
import LoggedInFooter from 'src/modules/logged-in-footer';
import styles from './logged-in-layout.module.scss';
import IdleTimer from 'react-idle-timer';
import { AuthContext } from 'src/context/ute-auth-context/auth-provider';
import SessionTimeout from '../../session-timeout/index';
import LoggedOut from '../../logged-out/index';
import {
	fetchAURegistration,
	fetchCompanyNameAndLogo,
	getPendoMetadata,
	getSessionTimeout,
	getUserRole,
	markLogout,
	validateRefreshToken,
} from '../../common/common-api';
import {
	RefreshTokenResponseVM,
	ICompanyProductsResponse,
	ProductType,
	ICompanyNameAndLogoResponse,
	IUserRoleResponse,
	UserRole,
	IPendoMetadata,
	IAURegistrationRequestModelResponse,
	IAURegistrationRequestModel,
	AccessibleModules,
	emptyAURegistrationRequest,
	accessibleModuleRouteMappings,
} from 'src/modules/common/common-types';
import { getCompanyProducts } from 'src/modules/common/common-api';
import { getIsK1DocumentsPresent } from 'src/modules/distribute-k1s/distribute-k1-api';
import { IK1DocumentsPresentResponse } from 'src/modules/distribute-k1s/distribute-k1-types';
import { UTENotifier } from 'src/modules/notification/ute-notifier';
import {
	DISTRIBUTE_K1_CONSTANTS,
	ERROR_COMPANY_NAME_LOGO,
	ERROR_FETCH_AURegistration,
	LOGGED_IN_LAYOUT_CONSTANTS,
	ProductStatus,
} from 'src/helper/constants';
import usePendo from 'src/hooks/usePendo';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import AxiosInterceptorsSetup from 'src/modules/axios-interceptor';
import SignalRWebSocket from 'src/modules/signalr';
import UserNotification from 'src/modules/user-notifications';
import ThirdPartyUserToaster from 'src/modules/third-party-login/third-party-user-toaster';
import ThirdPartySessionTimeout from 'src/modules/third-party-login/third-party-session-timeout';
import { getThirdPartyActiveSessionTimeout } from 'src/modules/third-party-login/third-party-login-component-api';
import UTEModalLayout from 'src/components/ute-modal-layout';

declare global {
	interface Window {
		pendo: any;
	}
}

const LoggedInLayout = ({ children }: React.PropsWithChildren) => {
	const {
		authToken,
		updateAuthToken,
		refreshToken,
		updateRefreshToken,
		updateSessionTimeInSeconds,
		userDetail,
		companyProducts,
		updateIsSessionTimedOut,
		updateCompanyProducts,
		companyId,
		companyNameAndLogo,
		updateCompanyNameAndLogo,
		updateIsClientView,
	} = useContext(AuthContext);
	const WarningVisibleMinutes = 1;
	let idleTimer: any;
	const [showSessionTimeOut, setShowSessionTimeOut] = useState(false);
	const [showLoggedOut, setShowLoggedOut] = useState(false);
	const [sessionTimeInSeconds, setSessionTimeInSeconds] = useState(0);
	const [thirdPartyActiveSessionTimeInMinutes, setThirdPartyActiveSessionTimeInMinutes] = useState(0);
	const [showDistributeK1Menu, setShowDistributeK1Menu] = useState(false);
	const [showTaxpaymentMenu, setShowTaxPaymentMenu] = useState(false);
	const navigate = useNavigate();
	const [pendoMetadata, setPendoMetadata] = useState<IPendoMetadata>({ isTestCompany: false, subscription: '' });
	const [aURegistrationRequestModel, setAURegistrationRequestModel] =
		useState<IAURegistrationRequestModel>(emptyAURegistrationRequest);
	const [accessibleModulesList, setAccessibleModulesList] = useState<number[]>([]);
	const [showThirdPartySessionExpiryPopup, setShowThirdPartySessionExpiryPopup] = useState(false);
	const location = useLocation();
	const [isDocumentsTabAccessible, setIsDocumentsTabAccessible] = useState(false);
	const [isTaxDocumentsTabAccessible, setIsTaxDocumentsTabAccessible] = useState(false);

	usePendo({
		companyId,
		companyName: companyNameAndLogo.name,
		userId: userDetail.id,
		name: userDetail.name,
		email: userDetail.emailAddress,
		isTestCompany: pendoMetadata?.isTestCompany ?? false,
		subscription: pendoMetadata?.subscription ?? '',
	});

	useEffect(() => {
		fetchUserRole();
		if (userDetail?.auRegistrationId && userDetail.auRegistrationId > 0) {
			fetchAURegistration(
				Number(userDetail.auRegistrationId),
				(aURegistrationRequestModelResponse: IAURegistrationRequestModelResponse) => {
					if (aURegistrationRequestModelResponse.error) {
						UTENotifier.Error(ERROR_FETCH_AURegistration);
					} else {
						if (aURegistrationRequestModelResponse.aURegistrationRequestModel) {
							setAURegistrationRequestModel(aURegistrationRequestModelResponse.aURegistrationRequestModel);
							const accessibleModulesList =
								aURegistrationRequestModelResponse.aURegistrationRequestModel.accessibleModules.split(',').map(Number);
							setAccessibleModulesList([...accessibleModulesList]);
							const pathArray = window.location.pathname.split('/');
							const pathName = pathArray[pathArray.length - 1];
							if (!!pathName) {
								const module = accessibleModulesList.find(
									(module) => module === accessibleModuleRouteMappings[pathName],
								);
								if (
									(module === undefined || module === null) &&
									accessibleModulesList.find((module) => module === AccessibleModules.All) === undefined
								) {
									const userAccessPath = matchPath('/access/:productType/:companyId', location.pathname);
									!userAccessPath && navigate('/access-denied');
								}
							}
							//when user has access to modules except default(home) module, redirect to first allowed module
							else if (
								pathName === '' &&
								accessibleModulesList.length > 0 &&
								accessibleModulesList.findIndex((module) => module === AccessibleModules.Home) === -1
							) {
								const allowedRoute =
									Object.keys(accessibleModuleRouteMappings).find((key) => {
										let index = accessibleModulesList.findIndex(
											(module) => module === accessibleModuleRouteMappings[key],
										);
										return index !== -1;
									}) || 'access-denied';
								navigate('/' + allowedRoute);
							}
						} else {
							updateAuthToken('');
							updateRefreshToken('');
							navigate('/access-denied');
						}
					}
				},
			);
		}
	}, []);

	const isK1DocumentPresentForAuUser = () => {
		if (accessibleModulesList.includes(AccessibleModules.DistributeK1s)) {
			getIsK1DocumentsPresent((k1DocumentsPresentResponse: IK1DocumentsPresentResponse) => {
				if (k1DocumentsPresentResponse.error) {
					UTENotifier.Error(DISTRIBUTE_K1_CONSTANTS.ERROR_CHECKING_K1_DETAILS_PRESENT_OR_NOT);
				} else {
					setShowDistributeK1Menu(k1DocumentsPresentResponse.isPresent);
				}
			});
		}
	};

	const isK1DocumentPresentForPrimaryUser = () => {
		getIsK1DocumentsPresent((k1DocumentsPresentResponse: IK1DocumentsPresentResponse) => {
			if (k1DocumentsPresentResponse.error) {
				UTENotifier.Error(DISTRIBUTE_K1_CONSTANTS.ERROR_CHECKING_K1_DETAILS_PRESENT_OR_NOT);
			} else {
				setShowDistributeK1Menu(k1DocumentsPresentResponse.isPresent);
			}
		});
	};

	const isTaxPaymentMenuPresentForAuUser = () => {
		if (accessibleModulesList.includes(AccessibleModules.TaxPayments)) {
			setShowTaxPaymentMenu(showPaymentMenu());
		}
	};

	useEffect(() => {
		if (companyProducts.length === 0) {
			updateCompanyProductsInfo();
		}

		if (sessionTimeInSeconds === 0) {
			getSessionTimeout((sessionTimeout: number) => {
				updateSessionTimeInSeconds(sessionTimeout);
				setSessionTimeInSeconds(sessionTimeout);
			});
		}

		if (userDetail?.auRegistrationId && userDetail.auRegistrationId > 0) {
			if (thirdPartyActiveSessionTimeInMinutes === 0) {
				getThirdPartyActiveSessionTimeout(userDetail.auRegistrationId, (sessionTimeout: number) => {
					const positiveSessionTimeout = Math.max(0, sessionTimeout);
					setThirdPartyActiveSessionTimeInMinutes(positiveSessionTimeout);
				});
			}
		}
	}, []);

	useEffect(() => {
		if (userDetail?.auRegistrationId && userDetail.auRegistrationId > 0) {
			isK1DocumentPresentForAuUser();
		} else {
			isK1DocumentPresentForPrimaryUser();
		}

		if (userDetail?.auRegistrationId && userDetail.auRegistrationId > 0) {
			isTaxPaymentMenuPresentForAuUser();
		} else {
			setShowTaxPaymentMenu(showPaymentMenu());
		}
		if (userDetail?.auRegistrationId && userDetail.auRegistrationId > 0) {
			setIsDocumentsTabAccessible(accessibleModulesList.includes(AccessibleModules.Documents));
			setIsTaxDocumentsTabAccessible(
				accessibleModulesList.includes(AccessibleModules.TaxPayments) && showPaymentMenu(),
			);
		}
	}, [accessibleModulesList]);

	useEffect(() => {
		fetchCompanyNameAndLogo(Number(companyId), (companyNameAndLogoResponse: ICompanyNameAndLogoResponse) => {
			if (companyNameAndLogoResponse.error) {
				UTENotifier.Error(ERROR_COMPANY_NAME_LOGO);
			} else {
				updateCompanyNameAndLogo(companyNameAndLogoResponse.companyNameAndLogo);
				document.title = companyNameAndLogoResponse.companyNameAndLogo?.name + ' : ssportal.com';
			}
		});
		getPendoMetadata(updatePendoMetadata);
	}, []);

	AxiosInterceptorsSetup(authToken, navigate);

	const fetchUserRole = () => {
		getUserRole((userRoleResponse: IUserRoleResponse) => {
			if (userRoleResponse.error) {
				UTENotifier.Error('Error while fetching user role');
			} else {
				const userRole = userRoleResponse.role;
				if (userRole === UserRole.CPA) {
					updateIsClientView(true);
				} else {
					updateIsClientView(false);
				}
			}
		});
	};

	const updatePendoMetadata = (data: IPendoMetadata) => {
		setPendoMetadata(data);
	};

	const updateCompanyProductsInfo = () => {
		getCompanyProducts((companyProductsResponse: ICompanyProductsResponse) => {
			if (companyProductsResponse.error) {
				UTENotifier.Error(LOGGED_IN_LAYOUT_CONSTANTS.ERROR_FETCHING_COMPANY_PRODUCTS);
			} else {
				updateCompanyProducts(companyProductsResponse.products);
			}
		});
	};

	const showPaymentMenu = (): boolean => {
		if (companyProducts && companyProducts.length > 0) {
			const ssrProduct = companyProducts.find((product) => product.productId === ProductType.Returns);
			const extProduct = companyProducts.find((product) => product.productId === ProductType.Extensions);
			if (ssrProduct && ssrProduct.isSubscribed && ssrProduct.productStatus === ProductStatus.Enabled) {
				return true;
			} else if (extProduct && extProduct.isSubscribed && extProduct.productStatus === ProductStatus.Enabled) {
				return true;
			}
		}
		return false;
	};

	const onStayAlive = () => {
		validateRefreshToken(
			{ accessToken: authToken, refreshToken: refreshToken, registrationId: userDetail.id },
			(response: RefreshTokenResponseVM) => {
				updateAuthToken(response.accessToken);
				updateRefreshToken(response.refreshToken);
				setShowSessionTimeOut(false);
			},
			() => {
				manualLogout();
			},
		);
	};

	const manualLogout = () => {
		markLogout(() => {
			updateAuthToken('');
			updateRefreshToken('');
			localStorage.removeItem('sessionEndTime');
			if (userDetail?.auRegistrationId && userDetail.auRegistrationId > 0) {
				const clientId = localStorage.getItem('clientId');
				navigate(`/third-party/login/${clientId}`);
			} else {
				navigate('/');
			}
		});
	};

	const autoLogout = () => {
		markLogout(() => {
			updateAuthToken('');
			updateRefreshToken('');
			localStorage.removeItem('sessionEndTime');
			navigate(`/logout/${companyId}/${userDetail.auRegistrationId}`);
		});
	};

	const memoizedThirdPartyUserToaster = useMemo(
		() => (
			<ThirdPartyUserToaster
				clientName={userDetail.name}
				activeSessionTimeInMinutes={thirdPartyActiveSessionTimeInMinutes}
				showThirdPartySessionExpiryPopup={showThirdPartySessionExpiryPopup}
				setShowThirdPartySessionExpiryPopup={setShowThirdPartySessionExpiryPopup}
				logout={autoLogout}
			/>
		),
		[userDetail.name, thirdPartyActiveSessionTimeInMinutes, showThirdPartySessionExpiryPopup],
	);

	const handleOnActive = () => {
		setShowSessionTimeOut(false);
	};

	const handleOnIdle = () => {
		if (userDetail.auRegistrationId > 0) {
			autoLogout();
		} else {
			setShowSessionTimeOut(true);
		}
	};

	const getTimeoutInMilliseconds = () => {
		if (userDetail.auRegistrationId > 0) {
			const sessionTimeInMs = 1000 * sessionTimeInSeconds;
			return sessionTimeInMs;
		}
		return 1000 * 60 * (sessionTimeInSeconds / 60 - WarningVisibleMinutes);
	};

	return (
		<>
			<SignalRWebSocket />
			<UserNotification
				isDocumentsTabAccessible={isDocumentsTabAccessible}
				isTaxDocumentsTabAccessible={isTaxDocumentsTabAccessible}
			/>
			{!showSessionTimeOut && sessionTimeInSeconds > 0 && (
				<IdleTimer
					key='idleTimer'
					ref={idleTimer}
					onActive={handleOnActive}
					element={document}
					onIdle={handleOnIdle}
					stopOnIdle={true}
					debounce={250}
					timeout={getTimeoutInMilliseconds()}
				/>
			)}
			<div className={styles.loggedInLayout}>
				{userDetail?.auRegistrationId && userDetail.auRegistrationId > 0 && memoizedThirdPartyUserToaster}
				<div
					className={
						userDetail?.auRegistrationId && userDetail.auRegistrationId > 0
							? styles.contentWithHeaderInfo
							: styles.content
					}>
					<LeftNavigation
						logout={manualLogout}
						showDistributeK1Menu={showDistributeK1Menu}
						showTaxpaymentMenu={!showTaxpaymentMenu ? showPaymentMenu() : showTaxpaymentMenu}
						auRegistrationId={userDetail.auRegistrationId || 0}
						accessibleModulesList={accessibleModulesList}
					/>
					{children}
				</div>
				<footer className={styles.footer}>
					<LoggedInFooter />
				</footer>
			</div>

			{showThirdPartySessionExpiryPopup && (
				<UTEModalLayout
					width='500'
					theme='lightblue'
					title='Session Expiring Soon'
					hide={() => {
						setShowThirdPartySessionExpiryPopup(false);
					}}
					onClickOutside={() => {}}>
					<ThirdPartySessionTimeout onSubmit={() => setShowThirdPartySessionExpiryPopup(false)} />
				</UTEModalLayout>
			)}

			{sessionTimeInSeconds > 0 && userDetail.auRegistrationId <= 0 && (
				<SessionTimeout
					countDownMinutes={WarningVisibleMinutes}
					showModal={showSessionTimeOut}
					logout={() => {
						setShowSessionTimeOut(false);
						setShowLoggedOut(true);
					}}
					onStayAlive={onStayAlive}></SessionTimeout>
			)}

			<LoggedOut
				logout={() => {
					updateIsSessionTimedOut(true);
					manualLogout();
				}}
				showModal={showLoggedOut}></LoggedOut>
		</>
	);
};

export default LoggedInLayout;
